import React, { useEffect, useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Alert,
    CircularProgress,
    useTheme,
    TextField,
    Chip,
    IconButton,
    Tooltip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    Divider,
    Avatar
} from '@mui/material';
import {
    Refresh,
    People,
    CheckCircle,
    Cancel,
    Schedule,
    ExpandMore,
    ExpandLess,
    Warning,
    AccessTime,
    ExitToApp,
    Notes
} from '@mui/icons-material';
import { Layout } from '@layout/Layout';
import useAttendanceStore from '@/store/Employee/AttendanceStore';

interface TeamMember {
    employee_id: string;
    employee_name: string;
    employee_email: string;
    employee_contact: string | null;
    attendance_date: string | null;
    punch_in: string | null;
    punch_out: string | null;
    total_hours: number | null;
    status: 'present' | 'absent' | 'half-day' | null;
    remarks: string | null;
}

const TeamAttendance: React.FC = () => {
    const theme = useTheme();
    const [selectedDate, setSelectedDate] = useState<string>(
        new Date().toISOString().split('T')[0]
    );
    const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
    const [showAbsentDialog, setShowAbsentDialog] = useState(false);

    const {
        teamAttendance,
        loading,
        error,
        fetchTeamAttendance,
        resetError
    } = useAttendanceStore();

    useEffect(() => {
        fetchTeamAttendance(selectedDate);
    }, [selectedDate]);

    const toggleCardExpansion = (employeeId: string) => {
        setExpandedCards(prev => {
            const newSet = new Set(prev);
            if (newSet.has(employeeId)) {
                newSet.delete(employeeId);
            } else {
                newSet.add(employeeId);
            }
            return newSet;
        });
    };

    const formatTime = (dateTime: string | null) => {
        if (!dateTime) return '-';
        const date = new Date(dateTime);
        return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    };

    const calculateHours = (member: TeamMember) => {
        if (member.total_hours) {
            return `${Number(member.total_hours).toFixed(2)}h`;
        }
        if (member.punch_in && !member.punch_out) {
            const punchIn = new Date(member.punch_in);
            const now = new Date();
            const diff = now.getTime() - punchIn.getTime();
            const hours = (diff / (1000 * 60 * 60)).toFixed(2);
            return `${hours}h`;
        }
        return '-';
    };

    const getStatusColor = (member: TeamMember) => {
        if (!member.attendance_date) return theme.palette.error.main;
        if (member.punch_in && !member.punch_out) return theme.palette.primary.main;
        if (member.punch_out) return theme.palette.success.main;
        return theme.palette.grey[500];
    };

    const getStatusText = (member: TeamMember) => {
        if (!member.attendance_date) return 'Absent';
        if (member.punch_in && !member.punch_out) return 'Working';
        if (member.punch_out) return 'Completed';
        return 'Unknown';
    };

    // Filter employees with daily updates (remarks)
    const employeesWithUpdates = teamAttendance.filter(member =>
        member.remarks && member.remarks.trim() !== ''
    );

    // Filter employees without daily updates
    const employeesWithoutUpdates = teamAttendance.filter(member =>
        !member.remarks || member.remarks.trim() === ''
    );

    const getUpdatedCount = () => {
        return employeesWithUpdates.length;
    };

    const getAbsentCount = () => {
        return teamAttendance.filter(member => !member.attendance_date).length;
    };

    const getInitials = (name: string) => {
        return name
            .split(' ')
            .map(word => word[0])
            .join('')
            .toUpperCase()
            .substring(0, 2);
    };

    return (
        <Layout>
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                {/* Header */}
                <Box sx={{ mb: 4 }}>
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 'bold',
                            color: theme.palette.text.primary,
                            mb: 1
                        }}
                    >
                        Team Attendance Feed
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{ color: theme.palette.text.secondary }}
                    >
                        View daily attendance updates for your reporting team
                    </Typography>
                </Box>

                {/* Error Message */}
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }} onClose={resetError}>
                        {error}
                    </Alert>
                )}

                {/* Date Selector and Stats */}
                <Box sx={{ mb: 3 }}>
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '280px 1fr' },
                        gap: 2
                    }}>
                        <TextField
                            type="date"
                            label="Select Date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                        <Box sx={{
                            display: 'flex',
                            gap: 2,
                            flexWrap: 'wrap',
                            alignItems: 'center'
                        }}>
                            <Tooltip title="Refresh">
                                <IconButton onClick={() => fetchTeamAttendance(selectedDate)} disabled={loading}>
                                    <Refresh />
                                </IconButton>
                            </Tooltip>

                            {/* Summary Cards */}
                            <Card sx={{ flex: 1, minWidth: 140 }}>
                                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <People sx={{ color: theme.palette.info.main, fontSize: 32 }} />
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">
                                                Total Team
                                            </Typography>
                                            <Typography variant="h5" fontWeight="bold">
                                                {teamAttendance.length}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                            <Card sx={{ flex: 1, minWidth: 140 }}>
                                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <CheckCircle sx={{ color: theme.palette.success.main, fontSize: 32 }} />
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">
                                                Updated
                                            </Typography>
                                            <Typography variant="h5" fontWeight="bold" color="success.main">
                                                {getUpdatedCount()}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                            <Card
                                sx={{
                                    flex: 1,
                                    minWidth: 140,
                                    cursor: employeesWithoutUpdates.length > 0 ? 'pointer' : 'default',
                                    '&:hover': employeesWithoutUpdates.length > 0 ? {
                                        boxShadow: 3
                                    } : {}
                                }}
                                onClick={() => employeesWithoutUpdates.length > 0 && setShowAbsentDialog(true)}
                            >
                                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Warning sx={{ color: theme.palette.warning.main, fontSize: 32 }} />
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">
                                                No Updates
                                            </Typography>
                                            <Typography variant="h5" fontWeight="bold" color="warning.main">
                                                {employeesWithoutUpdates.length}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Box>
                    </Box>
                </Box>

                {/* Team Attendance Feed */}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                        <CircularProgress size={60} />
                    </Box>
                ) : employeesWithUpdates.length === 0 ? (
                    <Card sx={{ py: 8 }}>
                        <CardContent>
                            <Box sx={{ textAlign: 'center' }}>
                                <Schedule sx={{ fontSize: 80, color: theme.palette.text.disabled, mb: 2 }} />
                                <Typography variant="h5" color="text.secondary" gutterBottom>
                                    No Daily Updates Yet
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    No team members have submitted their daily updates for {new Date(selectedDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                                </Typography>
                                {employeesWithoutUpdates.length > 0 && (
                                    <Button
                                        variant="outlined"
                                        startIcon={<Warning />}
                                        onClick={() => setShowAbsentDialog(true)}
                                        sx={{ mt: 3 }}
                                    >
                                        View {employeesWithoutUpdates.length} Employee{employeesWithoutUpdates.length !== 1 ? 's' : ''} Without Updates
                                    </Button>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        {employeesWithUpdates.map((member) => {
                            const isExpanded = expandedCards.has(member.employee_id);
                            const hasLongRemarks = (member.remarks?.length || 0) > 300;
                            const displayRemarks = isExpanded || !hasLongRemarks
                                ? member.remarks
                                : member.remarks?.substring(0, 300) + '...';

                            return (
                                <Card
                                    key={member.employee_id}
                                    sx={{
                                        border: `1px solid ${theme.palette.divider}`,
                                        borderLeft: `4px solid ${getStatusColor(member)}`,
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                            boxShadow: 4,
                                            borderLeftWidth: '6px'
                                        }
                                    }}
                                >
                                    <CardContent sx={{ p: 3 }}>
                                        {/* Header with Avatar and Info */}
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2.5 }}>
                                            <Avatar
                                                sx={{
                                                    bgcolor: getStatusColor(member),
                                                    width: 64,
                                                    height: 64,
                                                    fontSize: '1.4rem',
                                                    fontWeight: 'bold',
                                                    mr: 2.5
                                                }}
                                            >
                                                {getInitials(member.employee_name)}
                                            </Avatar>
                                            <Box sx={{ flexGrow: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                                    <Typography variant="h6" fontWeight="bold">
                                                        {member.employee_name}
                                                    </Typography>
                                                    <Chip
                                                        label={getStatusText(member)}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: getStatusColor(member),
                                                            color: 'white',
                                                            fontWeight: 'bold'
                                                        }}
                                                    />
                                                </Box>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                    {member.employee_email}
                                                </Typography>

                                                {/* Attendance Stats in Row */}
                                                <Box sx={{
                                                    display: 'flex',
                                                    gap: 2,
                                                    flexWrap: 'wrap'
                                                }}>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1,
                                                            px: 2,
                                                            py: 1,
                                                            borderRadius: 1.5,
                                                            bgcolor: theme.palette.success.main + '15',
                                                            border: `1px solid ${theme.palette.success.main}30`
                                                        }}
                                                    >
                                                        <AccessTime sx={{ fontSize: 18, color: theme.palette.success.main }} />
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1 }}>
                                                                Punch In
                                                            </Typography>
                                                            <Typography variant="body1" fontWeight="600" color="success.main">
                                                                {formatTime(member.punch_in)}
                                                            </Typography>
                                                        </Box>
                                                    </Box>

                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1,
                                                            px: 2,
                                                            py: 1,
                                                            borderRadius: 1.5,
                                                            bgcolor: member.punch_out
                                                                ? theme.palette.error.main + '15'
                                                                : theme.palette.grey[100],
                                                            border: `1px solid ${member.punch_out
                                                                ? theme.palette.error.main + '30'
                                                                : theme.palette.divider}`
                                                        }}
                                                    >
                                                        <ExitToApp sx={{ fontSize: 18, color: member.punch_out ? theme.palette.error.main : theme.palette.text.secondary }} />
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1 }}>
                                                                Punch Out
                                                            </Typography>
                                                            <Typography
                                                                variant="body1"
                                                                fontWeight="600"
                                                                color={member.punch_out ? "error.main" : "text.secondary"}
                                                            >
                                                                {formatTime(member.punch_out)}
                                                            </Typography>
                                                        </Box>
                                                    </Box>

                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1,
                                                            px: 2,
                                                            py: 1,
                                                            borderRadius: 1.5,
                                                            bgcolor: theme.palette.info.main + '15',
                                                            border: `1px solid ${theme.palette.info.main}30`
                                                        }}
                                                    >
                                                        <Schedule sx={{ fontSize: 18, color: theme.palette.info.main }} />
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1 }}>
                                                                Hours
                                                            </Typography>
                                                            <Typography variant="body1" fontWeight="600" color="info.main">
                                                                {calculateHours(member)}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Box>

                                        <Divider sx={{ my: 2.5 }} />

                                        {/* Daily Update Section */}
                                        <Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                                <Notes sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                                                <Typography variant="subtitle1" fontWeight="bold" color="primary">
                                                    Daily Update
                                                </Typography>
                                            </Box>
                                            <Box
                                                sx={{
                                                    p: 2.5,
                                                    borderRadius: 2,
                                                    bgcolor: theme.palette.background.default,
                                                    border: `1px solid ${theme.palette.divider}`,
                                                    lineHeight: 1.7,
                                                    color: theme.palette.text.primary,
                                                    '& p': {
                                                        margin: '0.5em 0',
                                                        '&:first-of-type': { marginTop: 0 },
                                                        '&:last-child': { marginBottom: 0 }
                                                    },
                                                    '& ul, & ol': {
                                                        paddingLeft: '1.5rem',
                                                        margin: '0.5em 0',
                                                        '& li': {
                                                            marginBottom: '0.25em'
                                                        }
                                                    },
                                                    '& strong': {
                                                        fontWeight: 700
                                                    },
                                                    '& em': {
                                                        fontStyle: 'italic'
                                                    }
                                                }}
                                                dangerouslySetInnerHTML={{ __html: displayRemarks || '' }}
                                            />

                                            {/* Expand/Collapse Button */}
                                            {hasLongRemarks && (
                                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                                                    <Button
                                                        size="small"
                                                        onClick={() => toggleCardExpansion(member.employee_id)}
                                                        endIcon={isExpanded ? <ExpandLess /> : <ExpandMore />}
                                                        sx={{
                                                            textTransform: 'none',
                                                            color: theme.palette.primary.main
                                                        }}
                                                    >
                                                        {isExpanded ? 'Show Less' : 'Show More'}
                                                    </Button>
                                                </Box>
                                            )}
                                        </Box>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </Box>
                )}

                {/* Dialog for Employees Without Updates */}
                <Dialog
                    open={showAbsentDialog}
                    onClose={() => setShowAbsentDialog(false)}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            maxHeight: '70vh',
                            height: '70vh',
                            display: 'flex',
                            flexDirection: 'column'
                        }
                    }}
                >
                    <DialogTitle>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Warning sx={{ color: theme.palette.warning.main }} />
                            <Typography variant="h6" fontWeight="bold">
                                Employees Without Daily Updates
                            </Typography>
                        </Box>
                    </DialogTitle>
                    <DialogContent sx={{ overflowY: 'auto', flex: 1 }}>
                        {employeesWithoutUpdates.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 3 }}>
                                <CheckCircle sx={{ fontSize: 64, color: theme.palette.success.main, mb: 2 }} />
                                <Typography variant="body1" color="text.secondary">
                                    All team members have submitted their daily updates!
                                </Typography>
                            </Box>
                        ) : (
                            <List>
                                {employeesWithoutUpdates.map((member, index) => (
                                    <React.Fragment key={member.employee_id}>
                                        <ListItem>
                                            <Avatar
                                                sx={{
                                                    bgcolor: theme.palette.grey[400],
                                                    mr: 2,
                                                    width: 40,
                                                    height: 40
                                                }}
                                            >
                                                {getInitials(member.employee_name)}
                                            </Avatar>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body1" fontWeight="500">
                                                        {member.employee_name}
                                                    </Typography>
                                                }
                                                secondary={
                                                    <Typography variant="body2" color="text.secondary">
                                                        {member.employee_email}
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>
                                        {index < employeesWithoutUpdates.length - 1 && <Divider />}
                                    </React.Fragment>
                                ))}
                            </List>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowAbsentDialog(false)}>Close</Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </Layout>
    );
};

export default TeamAttendance;